import { ShoppingBag, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardCartItem from '@/domains/mobile-web/components/CardCartItem/CardCartItem';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
    useCart,
    useRemoveCartItem,
    useRemoveCartItems,
    useUpdateCartItem,
} from '@/domains/mobile-web/hooks/useCart';
import type { CartItem } from '@/domains/mobile-web/types/cart.types';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Button from '@/shared/components/ui/Button';
import BottomPayment from '@/domains/mobile-web/components/BottomPayment/BottomPayment';

const MAX_QUANTITY = 99;

const Cart = () => {
    const navigate = useNavigate();
    const { data: cart, isLoading, isError } = useCart();
    const updateItem = useUpdateCartItem();
    const removeItem = useRemoveCartItem();
    const removeItems = useRemoveCartItems();

    // Selection is kept on this screen only; lines start selected
    const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());

    const items = cart?.items ?? [];
    const checkedItems = useMemo(
        () => (cart?.items ?? []).filter((item) => !unchecked.has(item.id)),
        [cart, unchecked],
    );
    const totalPrice = checkedItems.reduce(
        (sum, item) => sum + Number(item.unit_price) * item.quantity,
        0,
    );
    const isAllChecked = items.length > 0 && checkedItems.length === items.length;

    const handleCheckedChange = (checked: boolean, item: CartItem) => {
        setUnchecked((prev) => {
            const next = new Set(prev);
            if (checked) next.delete(item.id);
            else next.add(item.id);
            return next;
        });
    };

    const handleQuantityChange = (quantity: number, item: CartItem) => {
        updateItem.mutate({ itemId: item.id, quantity });
    };

    const handleRemove = (item: CartItem) => {
        removeItem.mutate(item.id);
    };

    const handleRemoveChecked = () => {
        if (checkedItems.length === 0) return;
        removeItems.mutate(checkedItems.map((item) => item.id));
    };

    const handleToggleAll = (checked: boolean) => {
        setUnchecked(checked ? new Set() : new Set(items.map((item) => item.id)));
    };

    const handleCheckout = () => {
        if (checkedItems.length === 0) return;
        const itemIds = checkedItems.map((item) => item.id).join(',');
        navigate(`/store/checkout?items=${encodeURIComponent(itemIds)}`);
    };

    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader type="back" onBack={() => navigate(-1)} title="Keranjang" />
            </HeaderMobile>

            <WrapperMobile className="mb-[120px]">
                {isLoading ? null : isError ? (
                    <p className="body1 text-text-grey">Gagal memuat keranjang.</p>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-16 text-center">
                        <ShoppingBag size={48} strokeWidth={1.5} className="text-text-grey" />
                        <p className="title2bold text-black">Keranjang masih kosong</p>
                        <p className="body1 text-text-grey">
                            Yuk, cari buku favoritmu dan tambahkan ke keranjang.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/store/book')}
                            className="mt-2 body1medium btn-fill-blue px-6"
                        >
                            Lihat Buku
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="w-full shadowCard p-3 inline-flex justify-between gap-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={(e) => handleToggleAll(e.target.checked)}
                                    className="h-5 w-5 cursor-pointer rounded-[4px] border border-blue-cs-200 accent-text-blue-100"
                                />
                                <span className="body1medium text-black">
                                    Pilih Semua ({items.length})
                                </span>
                            </label>
                            <Button
                                type="button"
                                onClick={handleRemoveChecked}
                                aria-label="Hapus item terpilih dari keranjang"
                                className="flex items-center gap-2 body1medium text-text-danger-500 transition hover:opacity-80"
                            >
                                <Trash2 size={18} strokeWidth={1.8} />
                                Hapus Terpilih
                            </Button>
                        </div>

                        {items.map((item) => (
                            <CardCartItem
                                key={item.id}
                                product={item}
                                checked={!unchecked.has(item.id)}
                                maxQuantity={MAX_QUANTITY}
                                onCheckedChange={handleCheckedChange}
                                onQuantityChange={handleQuantityChange}
                                onRemove={handleRemove}
                            />
                        ))}
                    </>
                )}
            </WrapperMobile>

            {items.length > 0 && (
                <BottomPayment
                    textButton='Beli Sekarang'
                    title='Total Pesanan'
                    disabled={checkedItems.length === 0}
                    onClick={handleCheckout}
                    totalPrice={totalPrice}
                />
            )}
        </main>
    );
};

export default Cart;
